import { Component, Input, forwardRef, HostListener, ElementRef, ViewChild, TemplateRef, ApplicationRef, EmbeddedViewRef, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'apple-multiselect',
  templateUrl: './apple-multiselect.component.html',
  styleUrls: ['./apple-multiselect.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppleMultiselectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class AppleMultiselectComponent implements ControlValueAccessor, OnDestroy {
  private static openInstance: AppleMultiselectComponent | null = null
  @Input() placeholder: string = 'Select options'
  @Input() options: string[] = []
  @Input() multiple: boolean = true // New input to control single/multi select
  @ViewChild('trigger', { static: false }) triggerRef?: ElementRef<HTMLDivElement>
  @ViewChild('dropdownTpl', { static: false }) dropdownTpl?: TemplateRef<any>

  public isOpen = false
  public selectedValues: string[] = []
  public selectedValue: string = '' // For single select mode
  private dropdownElement?: HTMLElement
  private viewRef?: EmbeddedViewRef<any>
  
  private onChange = (value: string[] | string) => {}
  private onTouched = () => {}

  constructor(private readonly appRef: ApplicationRef) {}

  public toggleDropdown(event?: Event): void {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    const willOpen = !this.isOpen
    if (willOpen) {
      if (AppleMultiselectComponent.openInstance && AppleMultiselectComponent.openInstance !== this) {
        AppleMultiselectComponent.openInstance.closeDropdown()
      }
      this.isOpen = true
      this.onTouched()
      this.attachGlobalPanel()
      AppleMultiselectComponent.openInstance = this
    } else {
      this.closeDropdown()
    }
  }

  @HostListener('document:focusin', ['$event'])
  public onFocusIn(event: FocusEvent): void {
    if (!this.isOpen) return
    const target = event.target as Node
    const insideTrigger = !!this.triggerRef?.nativeElement?.contains(target)
    const insideDropdown = !!this.dropdownElement?.contains(target as Node)
    if (!insideTrigger && !insideDropdown) {
      this.detachGlobalPanel()
      this.isOpen = false
    }
  }

  @HostListener('window:blur')
  public onWindowBlur(): void {
    if (!this.isOpen) return
    this.detachGlobalPanel()
    this.isOpen = false
  }

  @HostListener('document:visibilitychange')
  public onVisibilityChange(): void {
    if (document.hidden && this.isOpen) {
      this.detachGlobalPanel()
      this.isOpen = false
    }
  }

  private attachGlobalPanel(): void {
    if (!this.triggerRef?.nativeElement || !this.dropdownTpl) return
    // Create container and append to body
    const container = document.createElement('div')
    container.className = 'apple-multiselect-dropdown'
    document.body.appendChild(container)
    this.dropdownElement = container

    // Create an embedded Angular view from the template
    this.viewRef = this.dropdownTpl.createEmbeddedView({})
    this.appRef.attachView(this.viewRef)
    // Append all root nodes (the panel and its children) into the container
    this.viewRef.rootNodes.forEach((node: any) => container.appendChild(node))

    requestAnimationFrame(() => this.positionDropdown())
  }

  private detachGlobalPanel(): void {
    if (this.viewRef) {
      this.appRef.detachView(this.viewRef)
      this.viewRef.destroy()
      this.viewRef = undefined
    }
    if (this.dropdownElement?.parentElement === document.body) {
      document.body.removeChild(this.dropdownElement)
    }
    this.dropdownElement = undefined
  }

  private closeDropdown(): void {
    if (!this.isOpen) return
    this.detachGlobalPanel()
    this.isOpen = false
    if (AppleMultiselectComponent.openInstance === this) {
      AppleMultiselectComponent.openInstance = null
    }
  }

  private positionDropdown(): void {
    if (!this.triggerRef?.nativeElement || !this.dropdownElement) return

    const triggerRect = this.triggerRef.nativeElement.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth

    // Let the panel grow to fit its widest option (e.g. "Tijdelijk/Freelance/ZZP")
    // but never exceed the viewport. Trigger width is the floor.
    const el = this.dropdownElement
    el.style.position = 'fixed'
    el.style.width = 'max-content'
    el.style.minWidth = `${Math.max(triggerRect.width, 160)}px`
    el.style.maxWidth = `${Math.max(160, viewportWidth - 8)}px`

    const dropdownHeight = el.offsetHeight
    const dropdownWidth = el.offsetWidth

    let top = triggerRect.bottom + 4
    if (dropdownHeight && top + dropdownHeight > viewportHeight && triggerRect.top > dropdownHeight) {
      top = Math.max(4, triggerRect.top - dropdownHeight - 4)
    }

    let left = triggerRect.left
    const maxLeft = viewportWidth - dropdownWidth - 4
    if (left > maxLeft) {
      left = Math.max(4, maxLeft)
    }

    el.style.top = `${top}px`
    el.style.left = `${left}px`
    el.style.zIndex = '2147483647'
    el.style.opacity = '1'
    el.style.pointerEvents = 'auto'
  }

  public toggleOption(option: string, event?: Event): void {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    
    if (this.multiple) {
      // Multi-select mode
      const index = this.selectedValues.indexOf(option)
      if (index > -1) {
        this.selectedValues = this.selectedValues.filter(v => v !== option)
      } else {
        this.selectedValues = [...this.selectedValues, option]
      }
      this.onChange(this.selectedValues)
    } else {
      // Single-select mode
      this.selectedValue = option
      this.selectedValues = [option] // Keep selectedValues in sync for template
      this.onChange(option)
      // Close dropdown after selection in single-select mode
      this.closeDropdown()
    }
  }

  public isSelected(option: string): boolean {
    return this.selectedValues.includes(option)
  }

  public get displayText(): string {
    if (this.multiple) {
      if (this.selectedValues.length === 0) {
        return this.placeholder
      }
      return this.selectedValues.join(', ')
    } else {
      return this.selectedValue || this.placeholder
    }
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: Event): void {
    if (!this.isOpen) return
    const target = event.target as Node
    const insideTrigger = !!this.triggerRef?.nativeElement?.contains(target)
    const insideDropdown = !!this.dropdownElement?.contains(target as Node)
    if (!insideTrigger && !insideDropdown) {
      this.detachGlobalPanel()
      this.isOpen = false
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  public onEscape(event: Event): void {
    if (!this.isOpen) return
    event.preventDefault()
    this.detachGlobalPanel()
    this.isOpen = false
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  public onViewportChange(): void {
    if (this.isOpen) {
      this.positionDropdown()
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string[] | string): void {
    if (this.multiple) {
      this.selectedValues = (value as string[]) || []
    } else {
      this.selectedValue = (value as string) || ''
      this.selectedValues = this.selectedValue ? [this.selectedValue] : []
    }
  }

  registerOnChange(fn: (value: string[] | string) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    // Handle disabled state if needed
  }

  ngOnDestroy(): void {
    this.detachGlobalPanel()
  }
}
